/**
 * /sources/$connector/$accountId — detail view for a single connected account.
 *
 * Shows:
 *  - Header: connector icon + display name + normalized connection status pill.
 *  - Stats row: connection last-seen, data last-activity (distinct labels), message count.
 *  - Granola only: the account's meeting notes, with per-meeting ingestion toggles.
 *  - Recent messages section: sender, snippet, relative timestamp, collection (chat) name.
 *  - Danger zone: disconnect, plus Granola's delete-all-data.
 *
 * The page is read-only ABOUT the data and mutating only at the bottom: every
 * destructive action for an account lives here rather than on the /sources
 * list, so that page stays a scannable index instead of a row of trash icons
 * (see connector-danger-zone.tsx for what moved and why).
 *
 * Loading → Skeleton; zero messages → EmptyState; fetch error → inline destructive text.
 * "Load more" via cursor-based pagination (nextCursor from the messages endpoint).
 */
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { ChevronLeft, NotebookPen, Phone, Send, Slack } from 'lucide-react';
import { IconTile } from '@/components/patterns/icon-tile';
import { StatCard } from '@/components/patterns/stat-card';
import { StatusPill, type StatusPillTone } from '@/components/patterns/status-pill';
import {
  ConnectorDangerZone,
  GranolaDeleteAllData,
} from '@/components/sources/connector-danger-zone';
import { GranolaMeetingsPanel } from '@/components/sources/granola-meetings-panel';
import { formatRelative, MessageLog } from '@/components/sources/message-log';
import { buttonVariants } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { type NormalizedConnectionStatus, sourceAccountApi } from '@/lib/api';
import { qk } from '@/lib/query-client';
import { cn } from '@/lib/utils';

// Normalized connection status → pill tone and label.
const STATUS_MAP: Record<NormalizedConnectionStatus, { tone: StatusPillTone; label: string }> = {
  connected: { tone: 'active', label: 'Connected' },
  degraded: { tone: 'paused', label: 'Degraded' },
  error: { tone: 'danger', label: 'Error' },
  disconnected: { tone: 'neutral', label: 'Disconnected' },
  pending: { tone: 'neutral', label: 'Pending' },
};

// Connector name → lucide icon (matches the icon used in the account list cards).
function ConnectorIcon({ connector }: { connector: string }) {
  if (connector === 'slack') return <Slack className="h-5 w-5" />;
  if (connector === 'signal') return <Send className="h-5 w-5" />;
  if (connector === 'granola') return <NotebookPen className="h-5 w-5" />;
  return <Phone className="h-5 w-5" />;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function SourceDetailPage() {
  const { connector = '', accountId = '' } = useParams({ strict: false }) as {
    connector?: string;
    accountId?: string;
  };

  const accountQuery = useQuery({
    queryKey: qk.sourceAccount(connector, accountId),
    queryFn: () => sourceAccountApi.get(connector, accountId),
    enabled: Boolean(connector && accountId),
  });

  const messagesQuery = useInfiniteQuery({
    queryKey: qk.sourceAccountMessages(connector, accountId),
    queryFn: ({ pageParam }) =>
      sourceAccountApi.listMessages(connector, accountId, {
        limit: 50,
        before: pageParam,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: Boolean(connector && accountId),
  });

  const account = accountQuery.data;
  const allMessages = messagesQuery.data?.pages.flatMap((p) => p.items) ?? [];

  const statusInfo = account
    ? (STATUS_MAP[account.connection.status] ?? {
        tone: 'neutral' as StatusPillTone,
        label: account.connection.rawStatus,
      })
    : null;

  return (
    <div>
      {/* Back link */}
      <Link
        to="/sources"
        className={cn(
          buttonVariants({ variant: 'ghost', size: 'sm' }),
          '-ml-2 mb-4 inline-flex items-center gap-1 text-[13px] text-muted-foreground',
        )}
      >
        <ChevronLeft className="h-4 w-4" />
        Sources
      </Link>

      {/* Header */}
      {accountQuery.isLoading ? (
        <div className="flex items-center gap-4">
          <Skeleton className="h-[44px] w-[44px] rounded-[11px]" />
          <div className="flex flex-col gap-2">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-24" />
          </div>
        </div>
      ) : accountQuery.isError ? (
        <p className="text-sm text-destructive">
          Failed to load account —{' '}
          {(accountQuery.error as { message?: string })?.message ?? 'unknown error'}
        </p>
      ) : account ? (
        <div className="flex items-center gap-4">
          <IconTile tone="green">
            <ConnectorIcon connector={connector} />
          </IconTile>
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-[22px] font-bold tracking-[-0.02em]">
              {account.displayName}
            </h1>
            <p className="text-[13px] text-muted-foreground capitalize">{connector}</p>
          </div>
          {statusInfo && <StatusPill tone={statusInfo.tone}>{statusInfo.label}</StatusPill>}
        </div>
      ) : null}

      {/* Stats row */}
      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        {accountQuery.isLoading ? (
          <>
            <Skeleton className="h-[104px] rounded-xl" />
            <Skeleton className="h-[104px] rounded-xl" />
            <Skeleton className="h-[104px] rounded-xl" />
          </>
        ) : account ? (
          <>
            {/* "Being used" — live connection heartbeat */}
            <StatCard
              label="Connection last seen"
              value={formatRelative(account.connection.lastSeenAt)}
              sub="Time since Brain last heard from this account"
            />
            {/* "Last got information" — data activity, distinct from connection */}
            <StatCard
              label="Last message received"
              value={formatRelative(account.data.lastActivityAt)}
              sub="Most recent message ingested from any chat"
            />
            <StatCard
              label="Messages indexed"
              value={account.data.messageCount.toLocaleString()}
              sub={
                account.data.collectionCount > 0
                  ? // "chat" is wrong for a meeting-notes connector, and the
                    // count is the headline number a Granola user checks.
                    connector === 'granola'
                    ? `Across ${account.data.collectionCount} meeting${account.data.collectionCount === 1 ? '' : 's'}`
                    : `Across ${account.data.collectionCount} chat${account.data.collectionCount === 1 ? '' : 's'}`
                  : undefined
              }
            />
          </>
        ) : null}
      </div>

      {/* Granola's collections are meetings you manage individually, so they get
          a first-class list here — the other connectors surface chats on the
          /sources card instead. */}
      {connector === 'granola' && accountId ? <GranolaMeetingsPanel accountId={accountId} /> : null}

      {/* Recent messages */}
      <div className="mt-6">
        <MessageLog
          isLoading={messagesQuery.isLoading}
          isError={messagesQuery.isError}
          error={messagesQuery.error}
          messages={allMessages}
          hasNextPage={messagesQuery.hasNextPage}
          isFetchingNextPage={messagesQuery.isFetchingNextPage}
          onLoadMore={() => void messagesQuery.fetchNextPage()}
        />
      </div>

      {/* Danger zone — only once the account is known to exist, so a 404 does
          not render a Disconnect button for something unreachable. */}
      {account ? (
        <>
          <ConnectorDangerZone connector={connector} accountId={accountId} />
          {connector === 'granola' && <GranolaDeleteAllData accountId={accountId} />}
        </>
      ) : null}
    </div>
  );
}
